import { Injector, ModuleWithProviders, NgModule, inject, provideAppInitializer } from '@angular/core';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgIdleModule } from '@ng-idle/core';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { AppRoutingModule } from './app-routing.module';
import { LayoutModule } from './components/layout/layout.module';
import { CoreModule } from './core/core.module';
import { SharedModule } from './shared.module';
import { AnalyticsModule } from './components/analytics/analytics.module';
import { AdminModule } from './components/admin/admin.module';
import { CaseModule } from './components/case/case.module';
import { ReviewModule } from './components/review/review.module';
import { AppComponent } from './app.component';
import { AuthenticationInterceptor } from './interceptors/authentication.interceptor';
import { UserConfigs } from './models/user-configs.model';
import { UserConfigsService } from './services/user-configs.service';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { EulaDialogComponent } from './components/dialogs/eula-dialog/eula-dialog.component';
import { OkCancelDialogComponent } from './components/dialogs/ok-cancel/ok-cancel-dialog.component';
import { UserModule } from './components/user/user.module';
import { SelectFieldsDialogComponent } from './components/dialogs/select-fields/select-fields.component';
import { SelectSortComponent } from './components/dialogs/select-sort/select-sort.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SelectFilterComponent } from './components/dialogs/select-filter/select-filter.component';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTreeModule } from '@angular/material/tree';
import { CreateBriefcaseComponent } from './components/dialogs/create-briefcase/create-briefcase.component';
import { AddFieldComponent } from './components/dialogs/add-field/add-field.component';
import { MultiDocEditDialogComponent } from './components/dialogs/multi-doc-edit-dialog/multi-doc-edit-dialog.component';
import { SelectFolderComponent } from './components/dialogs/select-folder/select-folder.component';
import { QuickLinkManagementComponent } from './components/dialogs/quick-link-management/quick-link-management.component';
import { ManageDefaultsComponent } from './components/dialogs/manage-defaults/manage-defaults.component';
import { ManualUploadComponent } from './components/dialogs/manual-upload/manual-upload.component';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { SelectFilterNewComponent } from './components/dialogs/select-filter-new/select-filter-new.component';
import { FieldSearchComponent } from './components/dialogs/select-filter-new/field-search/field-search.component';
import { ProductionJobDialogComponent } from './components/dialogs/production-job-dialog/production-job-dialog.component';
import { DiscoveryModule } from './components/discovery/discovery.module';
import { AccountModule } from './components/account/account.module';
import { ProcessingModule } from './components/processing/processing.module';
import { DownloadComponent } from './components/download/download.component';
import { AddCaseFieldComponent } from './components/dialogs/add-case-field/add-case-field.component';
import { startupFactory } from './app.startup';
import { InRoleDirective } from './directives/in-role.directive';
import { ContextInterceptor } from './interceptors/context.interceptor';
import { ForgotPasswordComponent } from './components/identity/forgot-password/forgot-password.component';
import { LoginComponent } from './components/identity/login/login.component';
import { LoginChallengeComponent } from './components/identity/login-challenge/login-challenge.component';
import { LoginMfaSelectionComponent } from './components/identity/login-mfa-selection/login-mfa-selection.component';
import { ManagePasswordComponent } from './components/identity/manage-password/manage-password.component';
import { LightRopeComponent } from './components/common/light-rope/light-rope.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { StoreModule } from "@ngrx/store";
import { EffectsModule } from "@ngrx/effects";
import { PanelEffects } from "@store/doc-viewer/panel/panel.effects";
import { DocumentListEffects } from "@store/doc-viewer/document-list/document-list.effects";
import { CustomAiPromptComponent } from '@dialogs/custom-ai-prompt/custom-ai-prompt.component';
import { DocumentViewEffects } from "@store/doc-viewer/document-view/document-view.effects";
import { ActionPanelEffects } from '@store/doc-viewer/action-panel/action-panel.effects';
import { OpenAiSettingsEffects } from '@store/third-party/open-ai-settings/open-ai-settings.effects';
import { ThirdPartyEffects } from '@store/third-party/third-party.effects';
import { PromptsEffects } from '@store/ai-insights/prompts/prompts.effects';
import { RedactionEditorEffects } from '@store/doc-viewer/redaction-editor/redaction-editor.effects';
import { AnnotationEditorEffects } from "@store/doc-viewer/annotation-editor/annotation-editor.effects";
import { appReducers } from "@store/app.reducers";
import { GlobalEffects } from "@store/global/global.effects";
import { UserConfirmDownloadComponent } from './components/export/user-confirm-download/user-confirm-download.component';
import { OpenAiLinkEffects } from '@store/third-party/open-ai-link/open-ai-link.effects';
import { BaseDialogComponent } from "@dialogs/v2/base-dialog/base-dialog.component";
import { SummaryPromptsEffects } from '@store/ai-insights/summary-prompts/summary-prompts.effects';
import { ServiceScalingDialogComponent } from "@dialogs/service-scaling/service-scaling-dialog.component";
import { MatSlider } from "@angular/material/slider";
import { DocViewerModule } from "./components/doc-viewer-new/doc-viewer.module";

@NgModule({ declarations: [
        AppComponent,
        DashboardComponent,
        EulaDialogComponent,
        OkCancelDialogComponent,
        SelectFieldsDialogComponent,
        SelectFieldsDialogComponent,
        SelectSortComponent,
        SelectFilterComponent,
        SelectFilterNewComponent,
        FieldSearchComponent,
        QuickLinkManagementComponent,
        CreateBriefcaseComponent,
        AddFieldComponent,
        MultiDocEditDialogComponent,
        SelectFolderComponent,
        ManageDefaultsComponent,
        ManualUploadComponent,
        ProductionJobDialogComponent,
        DownloadComponent,
        AddCaseFieldComponent,
        LoginComponent,
        ForgotPasswordComponent,
        LoginChallengeComponent,
        LoginMfaSelectionComponent,
        ManagePasswordComponent,
        LightRopeComponent,
        CustomAiPromptComponent,
        UserConfirmDownloadComponent,
        BaseDialogComponent,
        ServiceScalingDialogComponent,
    ],
    exports: [
        InRoleDirective,
    ],
    bootstrap: [AppComponent], imports: [LayoutModule,
        CoreModule,
        UserModule,
        AnalyticsModule,
        AdminModule,
        CaseModule,
        ReviewModule,
        SharedModule,
        MatSlideToggleModule,
        NgIdleModule.forRoot(),
        [BrowserAnimationsModule],
        DragDropModule,
        AppRoutingModule,
        MatNativeDateModule,
        MatDatepickerModule,
        FormsModule,
        MatTreeModule,
        MatProgressBarModule,
        ReactiveFormsModule,
        NgxSkeletonLoaderModule,
        MatAutocompleteModule,
        DiscoveryModule,
        AccountModule,
        ProcessingModule,
        StoreModule.forRoot(appReducers),
        EffectsModule.forRoot([GlobalEffects, PanelEffects, DocumentListEffects, DocumentViewEffects, ActionPanelEffects, ThirdPartyEffects, OpenAiSettingsEffects, OpenAiLinkEffects, PromptsEffects, SummaryPromptsEffects, RedactionEditorEffects, AnnotationEditorEffects]),
        ScrollingModule,
        MatSlider,
        DocViewerModule], providers: [
        provideAppInitializer(() => {
        const initializerFn = (startupFactory)(inject(Injector));
        return initializerFn();
      }),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthenticationInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ContextInterceptor,
            multi: true
        },
        provideHttpClient(withInterceptorsFromDi()),
    ] })
export class AppModule {
	static forRoot(user: UserConfigs): ModuleWithProviders<AppModule> {
		return {
			ngModule: AppModule,
			providers: [
				{ provide: UserConfigsService, useValue: user }
			]
		};
	}
}
